import * as React from "react"
import { Box, Chip, FormControl, InputLabel, MenuItem, Select, } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { AXIOS_API_INSTANCE, get_service_order_part_color } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"
import { LoadingBackdropIndeterminate } from "../LoadingBackdrop";
import { useSnackbar } from "notistack";
import Chart from 'react-apexcharts'
import dayjs from "dayjs"
import { useTrackedUser } from "../UserState"
import { DataGrid, GridToolbar } from "@mui/x-data-grid"
import { sort_custom_statuses } from "../../Utils/service"

export default function ServiceOrderPartTurnTime({ startDatetime, endDatetime, selectedLocations }) {
    const { enqueueSnackbar } = useSnackbar();
    const [my_user,] = useTrackedUser();
    const [isLoading, setIsLoading] = React.useState(false)
    const [turnTimes, setTurnTimes] = React.useState({})
    const [selectedStep, setSelectedStep] = React.useState(1)
    const [fromStatus, setFromStatus] = React.useState(Object.keys(my_user.client.service_order_parts_statuses ?? {}).find((x) => my_user.client.service_order_parts_statuses[x].type === 'CREATED'))
    const [toStatus, setToStatus] = React.useState(Object.keys(my_user.client.service_order_parts_statuses ?? {}).find((x) => my_user.client.service_order_parts_statuses[x].type === 'INSTALLED'))

    const getReport = (start_datetime, end_datetime, location_ids) => {
        if (!location_ids || location_ids.length === 0) {
            return
        }
        setIsLoading(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("reports/serviceorder_parts_status_times", {
                params: {
                    start_datetime: start_datetime,
                    end_datetime: end_datetime.add(1, 'day'),
                    location_ids: location_ids,
                    status_1: fromStatus,
                    status_2: toStatus,
                },
            }).then((report) => {
                setTurnTimes(report.data.data)
                setIsLoading(false)
            }).catch((error) => {
                setIsLoading(false)
                enqueueSnackbar(`Failed to load data <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    React.useEffect(() => {
        getReport(startDatetime, endDatetime, selectedLocations.map((x) => x.id))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDatetime, endDatetime, selectedLocations, fromStatus, toStatus])

    const series = []
    const days = []
    for (let i = startDatetime; i <= endDatetime; i = i.add(1, 'day')) {
        days.push(i.startOf('day'))
    }

    Object.keys(turnTimes).sort().forEach((location_id) => {
        const loc = selectedLocations.find((loc) => loc.id.toString() === location_id)
        const range = {
            type: 'rangeArea',
            name: `${loc?.name} Min/Max`,
            data: []
        }
        const line = {
            type: 'line',
            name: `${loc?.name} Average`,
            data: []
        }
        days.forEach((d) => {
            const data = turnTimes[location_id][d.toISOString().slice(0, -5)]
            if (data) {
                range.data.push({ x: d, y: [data.min_time / (60 * 60 * 24), data.max_time / (60 * 60 * 24)], count: data.count })
                line.data.push({ x: d, y: data.average_time / (60 * 60 * 24), count: data.count })
            } else {
                range.data.push({ x: d, y: [0, 0], count: 0 })
                line.data.push({ x: d, y: 0, count: 0 })
            }
        })
        if (selectedStep > 1) {
            let new_range_data = []
            let new_line_data = []
            for (let i = 0; i < days.length; i += selectedStep) {
                const min = Math.min(...range.data.slice(i, Math.min(i + selectedStep, days.length)).filter((d) => d.count > 0).map((d) => d.y[0]))
                const max = Math.max(...range.data.slice(i, Math.min(i + selectedStep, days.length)).filter((d) => d.count > 0).map((d) => d.y[1]))
                const count = range.data.slice(i, Math.min(i + selectedStep, days.length)).reduce((partialSum, d) => partialSum + d.count ?? 0, 0)
                const sum = line.data.slice(i, Math.min(i + selectedStep, days.length)).reduce((partialSum, d) => partialSum + ((d.count ?? 0) * (d.y ?? 0)), 0)
                new_range_data.push({ x: range.data[i].x, y: [isFinite(min) ? min : 0, isFinite(max) ? max : 0], count: count })
                new_line_data.push({ x: range.data[i].x, y: sum / (count || 1), count: count })
            }
            range.data = new_range_data
            line.data = new_line_data
        }
        series.push(range, line)
    })

    const table_data = series.length === 0 ? [] : series[0].data.map((data, idx) => { return { id: idx, date: data.x, min: data.y[0], max: data.y[1], average: series[1].data[idx].y, count: data.count } })

    return (
        <React.Fragment>
            <Box>
                <LoadingBackdropIndeterminate
                    open={isLoading}
                    text="Loading Turn Time Report"
                />
                {!isLoading && Object.keys(turnTimes).length === 0 &&
                    <h3>NO DATA</h3>
                }
                <Box>
                    <p>Average time between two statuses being set on a part. The date of the second status is used for the start/end date filtering.</p>
                    <Grid container spacing={1}>
                        <Grid>
                            <FormControl sx={{ height: '100%' }}>
                                <InputLabel id="group-time-category">Step</InputLabel>
                                <Select
                                    labelId="group-time-category"
                                    label="Step"
                                    placeholder=''
                                    onChange={(event) => {
                                        setSelectedStep(event.target.value)
                                    }}
                                    value={selectedStep}
                                    sx={{ width: '115px', height: '100%' }}
                                >
                                    {[1, 7, 30, 60, 90].map((n) =>
                                        <MenuItem key={`${n}`} value={n} disabled={n > days.length}
                                        >
                                            {`${n} Day${n > 1 ? 's' : ''}`}
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid>
                            <FormControl>
                                <InputLabel id="from-status">From Status</InputLabel>
                                <Select
                                    labelId="from-status"
                                    label="From Status"
                                    placeholder=''
                                    onChange={(event) => {
                                        setFromStatus(event.target.value)
                                    }}
                                    value={fromStatus}
                                    sx={{ width: '175px' }}
                                >
                                    {sort_custom_statuses(my_user.client.service_order_parts_statuses).map((s) =>
                                        <MenuItem key={s} value={s}
                                        >
                                            <Chip label={s} color={get_service_order_part_color(my_user.client.service_order_parts_statuses, s)} />
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid>
                            <FormControl>
                                <InputLabel id="to-status">To Status</InputLabel>
                                <Select
                                    labelId="to-status"
                                    label="To Status"
                                    placeholder=''
                                    onChange={(event) => {
                                        setToStatus(event.target.value)
                                    }}
                                    value={toStatus}
                                    sx={{ width: '175px' }}
                                >
                                    {sort_custom_statuses(my_user.client.service_order_parts_statuses).map((s) =>
                                        <MenuItem key={s} value={s}
                                        >
                                            <Chip label={s} color={get_service_order_part_color(my_user.client.service_order_parts_statuses, s)} />
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    {!isLoading && Object.keys(turnTimes).length > 0 &&
                        <Box>
                            <Chart
                                type="rangeArea" height={450}
                                options={{
                                    chart: {
                                        height: 450,
                                        type: 'rangeArea',
                                        width: '100%'
                                    },
                                    dataLabels: {
                                        enabled: true,
                                        formatter: function (val, opt) {
                                            if (opt.seriesIndex === 1) {
                                                if (val !== 0) {
                                                    return Math.ceil(val * 10) / 10;
                                                }
                                            }
                                        }
                                    },
                                    stroke: {
                                        curve: 'straight',
                                        width: selectedLocations.map((x) => [0, 2]).flat(),
                                        dashArray: [0, 0, 0, 0]
                                    },
                                    xaxis: {
                                        type: 'datetime',
                                        categories: days.filter((v, index) => (selectedStep === 1) || (index % selectedStep === 0)).map((day) => day.format('M/D/YYYY')),
                                        tickAmount: Math.ceil(days.length / selectedStep) - 1,
                                        labels: {
                                            formatter: function (val, timestamp) {
                                                return dayjs.utc(val).format("MM/DD/YYYY");
                                            }
                                        }
                                    },
                                    yaxis: {
                                        decimalsInFloat: 1,
                                        title: {
                                            text: 'Average Turn Time Days'
                                        }
                                    },
                                    fill: {
                                        opacity: selectedLocations.map((x) => [0.24, 1]).flat()
                                    },
                                    markers: {
                                        hover: {
                                            sizeOffset: 5
                                        }
                                    },
                                    legend: {
                                        onItemClick: {
                                            toggleDataSeries: false // TODO: Figure out why allowing this causes a crash
                                        },
                                    },
                                    // tooltip: {
                                    //     y: {
                                    //         formatter: function (val) {
                                    //             return `${Math.round(val * 10) / 10} days`
                                    //         }
                                    //     }
                                    // }
                                }}
                                series={series}
                            />
                            <DataGrid
                                rows={table_data}
                                slots={{ toolbar: GridToolbar }}
                                columns={[
                                    {
                                        field: "date",
                                        headerName: "Date",
                                        flex: 1,
                                        editable: false,
                                        sortable: true,
                                        valueFormatter: (value) => selectedStep > 1 ? `${value.format('M/D/YYYY')} - ${value.add(selectedStep - 1, 'day').format('M/D/YYYY')}` : `${value.format('M/D/YYYY')}`
                                    },
                                    ...["min", "max", "average", "count"].map((column) => {
                                        return {
                                            field: column,
                                            headerName: column.charAt(0).toUpperCase() + column.slice(1),
                                            flex: 1,
                                            editable: false,
                                            sortable: true,
                                            valueFormatter: (v) => v.toFixed(2)
                                        }
                                    })
                                ]}
                                autoHeight={true}
                                disableRowSelectionOnClick
                            />
                        </Box>
                    }
                </Box>
            </Box>
        </React.Fragment>
    )
}
